import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// "Invexis" é nome próprio (manual §3) — fica fora do JSON, usado literal como
// fallback de rota desconhecida (mesmo comportamento de antes).
const UNKNOWN_ROUTE_TITLE = 'Invexis';

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/': 'pageTitles.dashboard',
  '/dashboard': 'pageTitles.dashboard',
  '/contas': 'pageTitles.accounts',
  '/administracao/evolucao': 'pageTitles.platformEvolution',
  '/administracao/tenants': 'pageTitles.tenantManagement',
  '/devices': 'pageTitles.devices',
  '/controle-de-acesso': 'pageTitles.accessControl',
  '/usuarios/novo': 'pageTitles.newUser',
  '/alertas': 'pageTitles.alerts',
  '/relatorios': 'pageTitles.reports',
  '/relatorios/devices': 'pageTitles.devicesReport',
  '/relatorios/software': 'pageTitles.softwareReport',
  '/relatorios/user-activity': 'pageTitles.userActivityReport',
  '/relatorios/user-sessions': 'pageTitles.userSessionsReport',
  '/relatorios/atividade-usuario': 'pageTitles.userActivityReportAlt',
  '/relatorios/eventos': 'pageTitles.eventsReport',
  '/relatorios/historico-eventos': 'pageTitles.eventsHistory',
  '/relatorios/geolocalizacao': 'pageTitles.geolocationReport',
  '/configuracoes': 'pageTitles.settings',
};

const getPageTitleKey = (pathname: string): string | null => {
  if (PAGE_TITLE_KEYS[pathname]) {
    return PAGE_TITLE_KEYS[pathname];
  }

  if (pathname.startsWith('/contas/')) {
    return 'pageTitles.accountDetail';
  }

  if (pathname.startsWith('/administracao/tenants/')) {
    return 'pageTitles.tenantDetail';
  }

  if (pathname.startsWith('/devices/')) {
    return 'pageTitles.deviceDetail';
  }

  if (pathname.startsWith('/usuarios/') && pathname.endsWith('/editar')) {
    return 'pageTitles.editUser';
  }

  if (pathname.startsWith('/alertas/regras')) {
    return 'pageTitles.alertRules';
  }

  if (pathname.startsWith('/relatorios/geolocalizacao/')) {
    return 'pageTitles.geolocationHistory';
  }

  return null;
};

export const usePageTitle = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  // i18n.language na dependência (além de location.pathname) garante que o
  // document.title reaja à troca de idioma mesmo sem navegação — document.title
  // é efeito colateral fora do DOM, então não re-renderiza sozinho com o resto da UI.
  useEffect(() => {
    const key = getPageTitleKey(location.pathname);
    const pageTitle = key ? t(key) : UNKNOWN_ROUTE_TITLE;
    document.title = t('pageTitles.documentTitle', { page: pageTitle });
  }, [location.pathname, t, i18n.language]);
};
